import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminLayout from '../../components/Admin/AdminLayout'
import { AdminStatCardFlex } from './adminUi'
import { REJECT_REASONS, defaultFeatureAccessForRole, featureOptionsForRole } from '../../constants/featureAccess'
import { ModalClose } from '../../components/icons'

const EMAIL_TAG_LABEL = {
  work: 'Work',
  free: 'Free Email',
  unknown: 'Unknown Domain',
}

const PAGE_SIZE = 7

function splitIntoColumns(items, columns = 2) {
  const size = Math.ceil(items.length / columns)
  return Array.from({ length: columns }, (_, i) => items.slice(i * size, (i + 1) * size))
}

function FeatureAccessPicker({ options, featureAccess, onToggle }) {
  const [leftCol, rightCol] = splitIntoColumns(options, 2)

  const renderItem = (opt) => (
    <label key={opt.key} className="admin-permission-item">
      <input
        type="checkbox"
        checked={featureAccess[opt.key] !== false}
        onChange={() => onToggle(opt.key)}
      />
      <span>{opt.label}</span>
    </label>
  )

  return (
    <div className="admin-permission-grid">
      <div className="admin-permission-col">{leftCol.map(renderItem)}</div>
      <div className="admin-permission-col">{rightCol.map(renderItem)}</div>
    </div>
  )
}

function formatDateTime(d) {
  if (!d) return '—'
  const dt = new Date(d)
  return dt.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).replace(',', '')
}

function exportCsv(rows) {
  const header = ['Name', 'Email', 'Email Type', 'Company', 'Role', 'Status', 'Submitted']
  const lines = rows.map((r) => [
    r.display_name,
    r.work_email,
    r.email_tag,
    r.company_name || '',
    r.role,
    r.review_status,
    r.event_at || r.created_at,
  ].map((c) => `"${String(c || '').replace(/"/g, '""')}"`).join(','))
  const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'registrations.csv'
  a.click()
  URL.revokeObjectURL(url)
}

export default function AdminRegistration() {
  const [stats, setStats] = useState(null)
  const [rows, setRows] = useState([])
  const [roleFilter, setRoleFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [emailFilter, setEmailFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [reviewItem, setReviewItem] = useState(null)
  const [featureAccess, setFeatureAccess] = useState({})
  const [rejectTarget, setRejectTarget] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectNote, setRejectNote] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/registrations')
      setStats(res.data.stats)
      setRows(res.data.registrations || [])
      window.dispatchEvent(new Event('rs-admin-badges-changed'))
    } catch {
      toast.error('Failed to load registrations')
    }
  }, [])

  useEffect(() => { load() }, [load])

  const filtered = useMemo(() => rows.filter((r) => {
    if (roleFilter !== 'all' && r.role !== roleFilter) return false
    if (statusFilter !== 'all' && r.review_status !== statusFilter) return false
    if (emailFilter !== 'all' && r.email_tag !== emailFilter) return false
    if (search) {
      const q = search.toLowerCase()
      if (!r.display_name?.toLowerCase().includes(q)
        && !r.work_email?.toLowerCase().includes(q)
        && !r.company_name?.toLowerCase().includes(q)) return false
    }
    return true
  }), [rows, roleFilter, statusFilter, emailFilter, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => { setPage(1) }, [roleFilter, statusFilter, emailFilter, search])

  const resetFilters = () => {
    setRoleFilter('all')
    setStatusFilter('all')
    setEmailFilter('all')
    setSearch('')
  }

  const openReview = (item) => {
    setReviewItem(item)
    setFeatureAccess(item.feature_access || defaultFeatureAccessForRole(item.role))
  }

  const toggleFeature = (key) => {
    setFeatureAccess((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleApprove = async (item = reviewItem) => {
    if (!item) return
    setBusy(true)
    try {
      await axios.post(`/api/admin/registration-approval/${item.id}`, {
        decision: 'approved',
        featureAccess,
      })
      toast.success('Registration approved')
      setReviewItem(null)
      load()
    } catch {
      toast.error('Approval failed')
    } finally {
      setBusy(false)
    }
  }

  const savePermissions = async () => {
    if (!reviewItem) return
    setBusy(true)
    try {
      await axios.patch(`/api/admin/users/${reviewItem.id}/feature-access`, { featureAccess })
      toast.success('Permissions saved')
      load()
    } catch {
      toast.error('Failed to save permissions')
    } finally {
      setBusy(false)
    }
  }

  const quickApprove = async (item) => {
    setBusy(true)
    try {
      await axios.post(`/api/admin/registration-approval/${item.id}`, {
        decision: 'approved',
        featureAccess: item.feature_access || defaultFeatureAccessForRole(item.role),
      })
      toast.success('Registration approved')
      load()
    } catch {
      toast.error('Approval failed')
    } finally {
      setBusy(false)
    }
  }

  const confirmReject = async () => {
    if (!rejectTarget) return
    if (!rejectReason) {
      toast.error('Please select a rejection reason')
      return
    }
    const reasonMeta = REJECT_REASONS.find((r) => r.value === rejectReason)
    setBusy(true)
    try {
      await axios.post(`/api/admin/registration-approval/${rejectTarget.id}`, {
        decision: 'rejected',
        rejectReason: reasonMeta?.label || rejectReason,
        rejectNote: rejectNote.trim() || undefined,
      })
      toast.success('Registration rejected. Notification email sent.')
      setRejectTarget(null)
      setReviewItem(null)
      setRejectReason('')
      setRejectNote('')
      load()
    } catch {
      toast.error('Rejection failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AdminLayout>
      <div className="admin-stats-row cols-4">
        <AdminStatCardFlex
          icon="clock"
          cls="blue"
          label="Pending Review"
          value={stats?.pendingReview ?? 0}
          sub="Auto-timeout in 48h"
        />
        <AdminStatCardFlex
          icon="check"
          cls="green"
          label="Approved Today"
          value={stats?.approvedToday ?? 0}
          sub={stats?.approvedDeltaPct ? `${stats.approvedDeltaPct} vs yesterday` : 'No approvals yet today'}
        />
        <AdminStatCardFlex
          icon="moderation"
          cls="orange"
          label="Rejected Today"
          value={stats?.rejectedToday ?? 0}
          sub="Personal email flagged"
        />
        <AdminStatCardFlex
          icon="email"
          cls="purple"
          label="Total Registered"
          value={stats?.totalRegistered ?? 0}
          sub={`This month: ${stats?.newThisMonth ?? 0}`}
        />
      </div>

      <div className="admin-table-card">
        <div className="admin-table-header">
          <span className="admin-table-title">Registration Queue</span>
        </div>
        <div className="admin-filter-row">
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="all">All Roles</option>
            <option value="GP">GP</option>
            <option value="LP">LP</option>
            <option value="Startup">Startup</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <select value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)}>
            <option value="all">All Email Types</option>
            <option value="work">Work Email</option>
            <option value="free">Free Email</option>
            <option value="unknown">Unknown Domain</option>
          </select>
          <input
            type="text"
            placeholder="Search by name, email or company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="admin-btn sm secondary" onClick={() => exportCsv(filtered)}>
            Export CSV
          </button>
          <button type="button" className="admin-btn sm secondary" onClick={resetFilters}>
            Reset
          </button>
        </div>
        <table>
          <thead>
            <tr>
              <th style={{ width: '28%' }}>Applicant</th>
              <th style={{ width: '22%' }}>Company</th>
              <th style={{ width: '10%' }}>Role</th>
              <th style={{ width: '18%' }}>Time &amp; Status</th>
              <th className="admin-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((item) => {
              const roleKey = (item.role || '').toLowerCase()
              const tag = item.email_tag || 'unknown'
              const tagLabel = EMAIL_TAG_LABEL[tag] || tag
              const isPending = item.review_status === 'pending'
              return (
                <tr key={item.id}>
                  <td>
                    <div className="admin-applicant-cell">
                      <span className="name">{item.display_name}</span>
                      <div className="email-row">
                        <span className="email">{item.work_email}</span>
                        <span className={`admin-email-tag ${tag}`}>
                          {tag === 'work' ? '✓ ' : tag === 'free' ? '✗ ' : ''}
                          {tagLabel}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="admin-company-cell">
                      <span className="name">{item.company_name || '— (Individual)'}</span>
                      <span className="type">{item.company_subtitle}</span>
                    </div>
                  </td>
                  <td><span className={`admin-role-badge ${roleKey}`}>{item.role}</span></td>
                  <td>
                    <div className="admin-time-status-cell">
                      <span className="time">{formatDateTime(item.event_at)}</span>
                      <span className={`admin-reg-status ${item.review_status}`}>
                        {item.review_status === 'pending' ? 'Pending' : item.review_status === 'approved' ? 'Approved' : 'Rejected'}
                      </span>
                    </div>
                  </td>
                  <td className="admin-td-actions">
                    <div className="admin-action-btns">
                      <button
                        type="button"
                        className="admin-btn admin-btn-sm admin-btn-ghost"
                        disabled={busy}
                        onClick={() => openReview(item)}
                      >
                        Review
                      </button>
                      {isPending && (
                        <>
                          <button
                            type="button"
                            className="admin-btn admin-btn-sm admin-btn-success"
                            disabled={busy}
                            onClick={() => quickApprove(item)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="admin-btn admin-btn-sm admin-btn-danger-ghost"
                            disabled={busy}
                            onClick={() => {
                              setRejectTarget(item)
                              setRejectReason('')
                              setRejectNote('')
                            }}
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {item.review_status === 'approved' && (
                        <span className="admin-btn admin-btn-sm admin-btn-ghost" style={{ opacity: 0.6, cursor: 'default' }}>
                          Approved
                        </span>
                      )}
                      {item.review_status === 'rejected' && (
                        <span className="admin-btn admin-btn-sm admin-btn-ghost" style={{ opacity: 0.6, cursor: 'default' }}>
                          Rejected
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
            {!pageItems.length && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: 32, color: 'var(--admin-text-muted)' }}>
                  No registrations match your filters
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="admin-pagination">
          <div className="admin-pagination-info">
            Showing {filtered.length ? (page - 1) * PAGE_SIZE + 1 : 0}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} registrations
          </div>
          <div className="admin-pagination-controls">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((n) => (
              <button key={n} type="button" className={page === n ? 'active' : ''} onClick={() => setPage(n)}>{n}</button>
            ))}
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
          </div>
        </div>
      </div>

      <div className="admin-policy-box">
        <h3>Platform Email &amp; Security Policy</h3>
        <div className="admin-policy-grid">
          <div className="admin-policy-item accepted">
            <h4>Accepted — Work Email</h4>
            <ul>
              <li><strong>✓</strong> Corporate domain (e.g. <strong>sequoiacap.com</strong>, <strong>gic.com</strong>)</li>
              <li><strong>✓</strong> University / institution domain (e.g. <strong>stanford.edu</strong>)</li>
              <li><strong>✓</strong> Government domain (e.g. <strong>gov.sg</strong>, <strong>gov.hk</strong>)</li>
              <li><strong>✓</strong> Verified custom domain — admin must verify ownership</li>
            </ul>
          </div>
          <div className="admin-policy-item rejected">
            <h4>Rejected — Free Email</h4>
            <ul>
              <li><strong>✗</strong> Gmail / Google Workspace (personal)</li>
              <li><strong>✗</strong> Outlook / Hotmail / MSN</li>
              <li><strong>✗</strong> QQ Mail / 163 / 126</li>
              <li><strong>✗</strong> Yahoo / iCloud / ProtonMail</li>
              <li><strong>✗</strong> Any other free public email provider</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="admin-policy-box" style={{ marginTop: 16 }}>
        <h3>Login — Work Email 2FA Requirement</h3>
        <div className="admin-policy-grid">
          <div className="admin-policy-item twofa">
            <h4>Every Login Requires Email Code</h4>
            <ul>
              <li>User enters email + password on login page</li>
              <li>Platform sends a <strong>4-digit code</strong> to registered work email</li>
              <li>Code valid for <strong>5 minutes</strong>, single-use</li>
              <li>After <strong>3 wrong attempts</strong>, account locked for 30 min</li>
              <li>No code = no access, even with correct password</li>
            </ul>
          </div>
          <div className="admin-policy-item why">
            <h4>Why This Matters</h4>
            <ul>
              <li>Prevents unauthorized access — even if password is leaked</li>
              <li>Ensures work email ownership — confirms user represents the org</li>
              <li>Full audit trail — each login logged with timestamp</li>
              <li>Blocks shared/hijacked accounts — only inbox owner can log in</li>
              <li>Simple &amp; secure — no app download needed, works globally</li>
            </ul>
          </div>
        </div>
      </div>

      {reviewItem && (
        <div className="admin-modal-overlay show" role="presentation" onClick={() => setReviewItem(null)}>
          <div className="admin-modal admin-modal-wide" role="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Review: {reviewItem.display_name}</h3>
              <ModalClose onClick={() => setReviewItem(null)} />
            </div>
            <div className="admin-modal-body">
              <div className="admin-review-meta-grid">
                <div className="admin-review-meta-block">
                  <p><strong>Email:</strong> {reviewItem.work_email}</p>
                  <p><strong>Company:</strong> {reviewItem.company_name || '—'}</p>
                  <p><strong>Role:</strong> {reviewItem.role}</p>
                </div>
                <div className="admin-review-meta-block">
                  <p><strong>Status:</strong> {reviewItem.review_status}</p>
                  {reviewItem.rejection?.reason && (
                    <p><strong>Last rejection:</strong> {reviewItem.rejection.reason}</p>
                  )}
                </div>
              </div>
              <div className="field admin-permission-field">
                <label className="admin-permission-field-label">Feature access — choose what this user can view</label>
                <FeatureAccessPicker
                  options={featureOptionsForRole(reviewItem.role)}
                  featureAccess={featureAccess}
                  onToggle={toggleFeature}
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn admin-btn-ghost" onClick={() => setReviewItem(null)}>Close</button>
              {reviewItem.review_status === 'approved' && (
                <button type="button" className="admin-btn admin-btn-sm admin-btn-success" disabled={busy} onClick={savePermissions}>
                  Save Permissions
                </button>
              )}
              {reviewItem.review_status === 'pending' && (
                <>
                  <button
                    type="button"
                    className="admin-btn admin-btn-sm admin-btn-danger-ghost"
                    disabled={busy}
                    onClick={() => {
                      setRejectTarget(reviewItem)
                      setRejectReason('')
                      setRejectNote('')
                    }}
                  >
                    Reject
                  </button>
                  <button type="button" className="admin-btn admin-btn-sm admin-btn-success" disabled={busy} onClick={() => handleApprove()}>
                    Approve &amp; Grant Access
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {rejectTarget && (
        <div className="admin-modal-overlay show" role="presentation" onClick={() => setRejectTarget(null)}>
          <div className="admin-modal admin-modal-scroll" role="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Reject Registration</h3>
              <ModalClose onClick={() => setRejectTarget(null)} />
            </div>
            <div className="admin-modal-body">
              <p style={{ fontSize: 12.5, color: 'var(--admin-text-sec)', marginBottom: 14 }}>
                Reject <strong>{rejectTarget.display_name}</strong>&apos;s registration. An email will be sent to <strong>{rejectTarget.work_email}</strong>.
              </p>
              <div className="field">
                <label htmlFor="rejectReason">Rejection reason *</label>
                <select id="rejectReason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}>
                  <option value="">Select a rejection reason...</option>
                  {REJECT_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="rejectNote">Additional note (optional, included in email)</label>
                <textarea
                  id="rejectNote"
                  rows={3}
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  placeholder="e.g. Please register again with your corporate email domain..."
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn admin-btn-ghost" onClick={() => setRejectTarget(null)}>Cancel</button>
              <button
                type="button"
                className="admin-btn admin-btn-sm"
                style={{ background: 'var(--admin-danger)', color: '#fff' }}
                disabled={busy}
                onClick={confirmReject}
              >
                Confirm rejection and send email
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
